import { Link, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import logo from "../assets/logo_basic.png";
import { useAuth } from "../context/AuthProvider";
import AppFooter from "../components/AppFooter";


const disclaimerSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.reignitespirit.com/disclaimer#webpage",
  url: "https://www.reignitespirit.com/disclaimer",
  name: "Platform Disclaimer",
  description:
    "Important limitations relating to Reignite Spirit reports, calculators, career intelligence and educational content.",
  isPartOf: {
    "@id": "https://www.reignitespirit.com/#website",
  },
};

export default function DisclaimerPage() {
  const navigate = useNavigate();
  const { session } = useAuth();

  return (
    <div className="min-h-screen bg-[#0F0A24] text-white">
      <SEO
        title="Platform Disclaimer"
        description="Read the limitations that apply to Reignite Spirit career reports, financial calculators, labour-market information and employment-related guidance."
        path="/disclaimer"
        image="/og-disclaimer.jpg"
        type="website"
        author="Reignite Spirit"
        keywords="Reignite Spirit disclaimer, career report disclaimer, financial calculator disclaimer, employment information limitations"
        schema={disclaimerSchema}
      />

      {/* =====================================================
          MAIN WEBSITE HEADER
      ===================================================== */}
      <header
        className="
          sticky
          top-0
          z-50
          border-b
          border-slate-800
          bg-[#11111f]/95
          backdrop-blur
        "
      >
        <div
          className="
            max-w-7xl
            mx-auto
            px-4
            sm:px-6
            lg:px-8
            py-3
            flex
            items-center
            justify-between
            gap-4
          "
        >
          {/* LOGO */}
          <button
            type="button"
            onClick={() => navigate("/landing")}
            className="flex items-center gap-3 cursor-pointer text-left shrink-0"
            aria-label="Go to Reignite Spirit home page"
          >
            <img
              src={logo}
              alt="Reignite Spirit logo"
              className="w-10 h-10 object-contain"
              width="40"
              height="40"
            />

            <div>
              <div className="text-[#EB6AB5] font-semibold">
                REIGNITE.SPIRIT
              </div>

              <p className="text-xs text-[#EB6AB5]/80 uppercase tracking-widest">
                Rekindle Purpose
              </p>
            </div>
          </button>

          {/* DESKTOP NAVIGATION */}
          <nav className="hidden lg:flex items-center gap-5 text-sm">
            <Link
              to="/landing"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Home
            </Link>

            <Link
              to="/snapshot"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Snapshot
            </Link>

            <Link
              to="/deep-audit"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Deep Audit
            </Link>

            <Link
              to="/blog"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              Career Intelligence
            </Link>

            <Link
              to="/faq"
              className="text-white/80 hover:text-[#EB6AB5] transition"
            >
              FAQ
            </Link>

            {/* LOGGED-IN USER */}
            {session ? (
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="
                  px-4
                  py-2
                  rounded-lg
                  border
                  border-white/20
                  text-white
                  hover:bg-white/10
                  transition
                "
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="
                    px-3
                    py-2
                    rounded-lg
                    border
                    border-white/20
                    text-white
                    hover:bg-white/10
                    transition
                  "
                >
                  Login
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="
                    px-3
                    py-2
                    rounded-lg
                    border
                    border-white/20
                    text-white
                    hover:bg-white/10
                    transition
                    whitespace-nowrap
                  "
                >
                  Register
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/choose-plan")}
                  className="
                    px-3
                    py-2
                    rounded-lg
                    bg-[#cb01bd]
                    hover:bg-[#e94ce0]
                    text-white
                    font-bold
                    transition
                    whitespace-nowrap
                  "
                >
                  Get My Audit
                </button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* =====================================================
          DISCLAIMER CONTENT
      ===================================================== */}
      <main className="px-6 py-16">
        <article className="max-w-4xl mx-auto">
          {/* PAGE HEADING */}
          <header className="text-center mb-14">
            <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.25em] text-sm">
              Disclaimer
            </p>

            <h1 className="text-4xl md:text-6xl font-extrabold mt-4">
              Important information and limitations
            </h1>

            <p className="mt-6 max-w-3xl mx-auto text-lg text-white/70 leading-relaxed">
              Reignite Spirit provides informational and decision-support
              tools. Please review the following limitations before relying on
              reports, calculators, articles or recommendations.
            </p>
          </header>

          <div className="space-y-6">
            {/* SECTION 1 */}
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                No guaranteed career outcome
              </h2>

              <p className="text-white/70 leading-relaxed">
                Career pathways, role matches and recommendations do not
                guarantee employment, interview selection, promotion, salary,
                business success or continued demand for a particular
                occupation.
              </p>
            </section>

            {/* SECTION 2 */}
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Not professional financial advice
              </h2>

              <p className="text-white/70 leading-relaxed">
                Financial runway calculations and related content are
                simplified estimates based on user inputs and implemented
                assumptions. They do not constitute financial, investment,
                tax, pension or debt advice.
              </p>
            </section>

            {/* SECTION 3 */}
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Not legal or employment advice
              </h2>

              <p className="text-white/70 leading-relaxed">
                Information about notice periods, severance, gratuity,
                unemployment benefits, immigration, visas, employment rights
                or workplace obligations is general information only.
                Applicable rules depend on jurisdiction, employment contract,
                employer policy and individual circumstances.
              </p>
            </section>

            {/* SECTION 4 */}
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Data and market limitations
              </h2>

              <p className="text-white/70 leading-relaxed">
                Labour-market, salary, hiring and automation information may be
                incomplete, estimated, delayed or unavailable for a particular
                occupation or location. Conditions can change without notice.
              </p>
            </section>

            {/* SECTION 5 */}
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                AI-generated and automated outputs
              </h2>

              <p className="text-white/70 leading-relaxed">
                Some outputs may be generated or supported by automated systems
                and artificial intelligence. Such systems can produce
                inaccurate, incomplete or unsuitable information. Users should
                independently verify material recommendations.
              </p>
            </section>

            {/* SECTION 6 */}
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                User responsibility
              </h2>

              <p className="text-white/70 leading-relaxed">
                Users remain responsible for their career, financial, legal,
                educational and employment decisions. Important decisions
                should be checked against current official information and
                discussed with qualified professionals where appropriate.
              </p>
            </section>

            {/* WARNING */}
            <section className="rounded-3xl border border-amber-400/30 bg-amber-400/5 p-7">
              <h2 className="text-2xl font-bold text-amber-100 mb-4">
                Before acting on important information
              </h2>

              <p className="text-amber-100/80 leading-relaxed">
                Verify salary, qualification, licensing, visa, tax, benefit,
                gratuity, severance and employment-right information through
                official or professionally qualified sources before making a
                material decision.
              </p>
            </section>

            {/* RELATED POLICIES */}
            <section className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <h2 className="text-2xl font-bold mb-4">
                Related policies
              </h2>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/terms-conditions"
                  className="px-5 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold"
                >
                  Terms and Conditions
                </Link>

                <Link
                  to="/privacy-cookie"
                  className="px-5 py-3 rounded-full border border-white/15 hover:border-[#EB6AB5] transition"
                >
                  Privacy and Cookie Policy
                </Link>

                <Link
                  to="/methodology"
                  className="px-5 py-3 rounded-full border border-white/15 hover:border-[#EB6AB5] transition"
                >
                  Methodology
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>
      <AppFooter
  variant="landing"
  logoSrc={logo}
/>
    </div>
  );
}